<script setup lang="ts">
import Modal from "@/Components/Modal.vue";
import {ref, watch} from "vue";
import {router} from "@inertiajs/vue3";
import {route} from "ziggy-js";
import axios from "axios";

const props = defineProps<{
    isOpen: boolean;
    selected_user?: {
        id: number;
        name: string;
        displayName: string;
        hasVerifiedBadge: boolean;
        avatarUrl?: string;
    } | null;
}>();

defineEmits<{
    (e: 'onToggle', payload: boolean): void
}>()

// State
interface LastOrder { amount: number; created_at: string | null }
const isLoading = ref(false);
const hasError = ref(false);
const errorMessage = ref('');
const orders = ref<LastOrder[]>([]);
const totalOrders = ref<number>(0);
const totalRobux = ref<number>(0);
let loadedForUserId: number | null = null; // avoid duplicate loads per open

const logout = () => {
    router.post(route('roblox.logout'), null, {
        onSuccess: () => {
            //refresh the page
            window.location.reload();
        }
    });
};

const formatDate = (iso: string | null) => {
    if(!iso) return '';
    try {
        const d = new Date(iso);
        return d.toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' });
    } catch { return iso || ''; }
};

const loadLastOrders = async () => {
    if (!props.selected_user?.id) return;
    if (loadedForUserId === props.selected_user.id && orders.value.length) return; // already loaded
    isLoading.value = true;
    hasError.value = false;
    errorMessage.value = '';
    try {
        const response = await axios.post(route('roblox.last-orders'));
        const data = response.data;
        orders.value = data.orders || [];
        totalOrders.value = data.total_orders || 0;
        totalRobux.value = data.total_robux || 0;
        loadedForUserId = props.selected_user.id;
    } catch (e: any) {
        console.error('Error fetching last orders:', e);
        hasError.value = true;
        errorMessage.value = e?.response?.data?.message || 'Failed to load orders';
    } finally {
        isLoading.value = false;
    }
};

watch(() => props.isOpen, (open) => {
    if (open) {
        loadLastOrders();
    }
});

watch(() => props.selected_user?.id, (id) => {
    if (props.isOpen && id) {
        loadLastOrders();
    }
});
</script>

<template>
    <Modal :show="isOpen" max-width="md" modalClass="h-[90dvh]">
        <div class="py-5 px-3 flex flex-col gap-3 bg-slate-200 z-20 h-full">
            <div class="w-full flex justify-between">
                <div class="py-3 px-4 bg-slate-200 flex items-center rounded-lg gap-2">
                    <img :src="props.selected_user?.avatarUrl || '/assets/img/roblox/roblox-man.webp'"
                         alt="roblox-man"
                         style="width: 60px; height: 60px">
                    <div class="flex flex-col">
                        <p class="font-sans font-medium text-xl text-black">
                            {{ props.selected_user?.displayName || $t('Unknown User') }}
                        </p>
                        <p class="font-sans font-medium text-base text-black">
                            @{{ props.selected_user?.name || 'unknown' }}
                        </p>
                    </div>
                </div>
                <button  @click="$emit('onToggle', false)">
                    <img src="/assets/img/roblox/close.svg" alt="close">
                </button>
            </div>
            <div>
                <div class="flex gap-5 flex-col w-full">
                    <p class="font-sans px-2.5 font-medium text-lg text-black">
                        <span class="uppercase">
                            {{ $t('Total') }}
                        </span>: {{ totalOrders }} {{ $t('Purchases') }}, {{ totalRobux.toLocaleString() }} R$
                    </p>

                    <p class="font-sans  px-2.5 font-medium text-lg text-black">
                        {{ $t('Recent purchases') }}:
                    </p>
                </div>
            </div>
            <div class="w-full bg-white rounded-2xl min-h-[160px] flex-1 flex items-stretch justify-center overflow-hidden">
                <!-- Loading state -->
                <div v-if="isLoading" class="flex flex-col justify-center items-center gap-3 py-10 w-full h-full">
                    <div class="animate-spin rounded-full h-16 w-16 border-b-2 border-violet-500"></div>
                    <p class="font-sans font-medium text-base">{{ $t('Loading purchases...') }}</p>
                </div>
                <!-- Error state -->
                <div v-else-if="hasError" class="flex flex-col justify-center items-center gap-2 py-6 px-4 text-center w-full h-full">
                    <p class="font-sans font-medium text-base text-red-500">{{ $t('Error loading purchases') }}</p>
                    <p class="text-sm text-gray-600">{{ errorMessage }}</p>
                    <button class="mt-2 px-4 py-2 bg-violet-500 text-white rounded hover:bg-violet-600 transition-colors" @click="loadLastOrders">{{ $t('Try again') }}</button>
                </div>
                <!-- Empty -->
                <div v-else-if="!orders.length" class="flex flex-col justify-center items-center gap-2 py-6 px-4 w-full h-full text-center">
                    <p class="font-sans font-medium text-base">{{ $t('No purchases yet') }}</p>
                </div>
                <!-- Orders list -->
                <div v-else class="w-full h-full overflow-y-auto rounded-2xl">
                    <div v-for="(order, idx) in orders" :key="idx" class="px-[17px] [&:not(:last-child)]:border-b [&:not(:last-child)]:border-slate-200 flex justify-between items-center py-[18px] w-full">
                        <p class="font-sans font-bold text-base pl-2 text-black">
                            {{ formatDate(order.created_at) }}
                        </p>
                        <p class="font-sans font-bold text-2xl text-black">
                            {{ order.amount.toLocaleString() }} R$
                        </p>
                    </div>
                </div>
            </div>
            <div class="flex justify-end py-3.5">
                <button class="flex gap-1.5 items-center" @click="logout">
                    <span class="font-sans uppercase text-slate-500 font-medium">
                        {{ $t('Logout') }}
                    </span>
                    <img src="/assets/img/roblox/exit.svg" alt="exit">
                </button>
            </div>
        </div>
    </Modal>
</template>
